import { useCallback, useMemo } from "react";
import { Form, useToast } from "@ir/ui";
import {
  CreateMasterDataProductCategoryDTO,
  UpdateMasterDataProductCategoryDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataProductCategory } from "@src/domain/entities/masterData";
import {
  CreateMasterDataProductCategoryUseCase,
  UpdateMasterDataProductCategoryUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataProductCategoryRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataProductCategories } from "@src/presentation/providers/MasterDataProductCategoriesDataProvider";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataProductCategoryAddEditForm,
  type ProductCategoryAddEditFormValues,
} from "../utils/MasterDataProductCategoryAddEditForm";
import { MasterDataProductCategoriesTable } from "../utils/MasterDataProductCategoriesTable";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import { getMasterDataSaveErrorMessage } from "../utils/masterDataSaveErrors";
import {
  isProductCategoryKeyDuplicateError,
  PRODUCT_CATEGORY_KEY_ALREADY_EXISTS_MESSAGE,
} from "../utils/productCategoryKeyValidation";
import {
  formatMasterDataRecordCount,
  useMasterDataAddEditModal,
} from "../utils/useMasterDataAddEditModal";

const EMPTY_FORM_VALUES: ProductCategoryAddEditFormValues = {
  name: "",
  categoryKey: "",
  description: "",
  searchVectorKeywords: "",
  isActive: true,
};

export const MasterDataProductCategoriesPage = () => {
  const {
    productCategories,
    loading,
    search,
    setSearch,
    total,
    setPage,
    refetch,
  } = useMasterDataProductCategories();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<ProductCategoryAddEditFormValues>();

  const {
    modalMode,
    editingEntity: editingCategory,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataProductCategory>();

  const repository = useMemo(
    () => new MasterDataProductCategoryRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataProductCategoryUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataProductCategoryUseCase(repository),
    [repository],
  );

  const initialValues = useMemo((): ProductCategoryAddEditFormValues => {
    if (modalMode === "edit" && editingCategory) {
      return {
        name: editingCategory.name,
        categoryKey: editingCategory.categoryKey,
        description: editingCategory.description ?? "",
        searchVectorKeywords: editingCategory.searchVectorKeywords ?? "",
        isActive: editingCategory.isActive,
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingCategory, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const handleSubmit = useCallback(
    async (values: ProductCategoryAddEditFormValues) => {
      setIsSaving(true);
      try {
        const name = values.name.trim();
        const categoryKey = values.categoryKey.trim();
        const description = values.description?.trim() || null;
        const searchVectorKeywords =
          values.searchVectorKeywords?.trim() || null;
        const isActive = Boolean(values.isActive);

        if (modalMode === "edit" && editingCategory) {
          await updateUseCase.execute(
            new UpdateMasterDataProductCategoryDTO({
              productCategoryId: editingCategory.productCategoryId,
              name,
              // Key is not editable in UI; always send the existing value.
              categoryKey: editingCategory.categoryKey,
              description,
              searchVectorKeywords,
              isActive,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataProductCategoryDTO({
              name,
              categoryKey,
              description,
              searchVectorKeywords,
              isActive,
            }),
          );
        }

        successToast({
          content: "Product category saved successfully",
        });
        closeModal();
        refetch();
      } catch (err) {
        if (isProductCategoryKeyDuplicateError(err)) {
          form.setFields([
            {
              name: "categoryKey",
              errors: [PRODUCT_CATEGORY_KEY_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save product category. Please try again.",
              "Failed to save product category. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingCategory,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  return (
    <>
      <MasterDataWritableListLayout
        title="Product Categories"
        subtitle={formatMasterDataRecordCount(loading, total)}
        searchPlaceholder="Search product categories"
        search={search}
        setSearch={setSearch}
        searchSection="product-categories"
        addButtonLabel="Add Category"
        onAdd={openCreateModal}
        loading={loading}
      >
        <MasterDataProductCategoriesTable
          productCategories={productCategories}
          search={search}
          setSearch={setSearch}
          setPage={setPage}
          onEdit={openEditModal}
        />
      </MasterDataWritableListLayout>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Category"
        editTitle="Edit Category"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataProductCategoryAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>
    </>
  );
};
